const _ = require("lodash");
const ParserHelpers = require("../util/ParserHelpers");
const SumStripsUtil = require("../../util/SumStripsUtil");
const colors = SumStripsUtil.getValueColors();
const ColorUtil = require("../../util/ColorUtil");
const diagramColors = ColorUtil.getDiagramColors();
const schemeColors = ColorUtil.getSharedColors();
const DrawRectUtil = require("../../util/DrawRectangleUtil");
const CanvasUtil = require("../../util/CanvasUtil");

const parser = function(ps) {
	let options = {
		terms: [], // override terms in equation (defaults to none)
		equationAbove: false, // display equation above strip (defaults to below)
		displayEquation: false, // display equation (defaults to true)
		displayBox: false, // display gray box (defaults to true)
		equalsPosition: "right", // position of the '=' sign, (defaults to "right")
		blankTerms: [], // terms in equation to replace with blanks (defaults to none)
	};
	options = ParserHelpers.parseNamedOptionalArgs(ps, options);

	// Process the options so they're in the right format if any were entered
	if (!_.isEmpty(options.terms)) {
		options.terms = options.terms.split(",");
	}
	options.equationAbove = options.equationAbove === "true";
	options.displayEquation = options.displayEquation === "true";
	options.displayBox = options.displayBox === "true";
	if (!_.isEmpty(options.blankTerms)) {
		options.blankTerms = options.blankTerms
			.split(",")
			.map(t => parseInt(t) - 1); // -1 because curriculum will enter "1" for the first term, "2" for the second, etc
	}

	const reqArgs = ParserHelpers.findArguments(ps, 1, {requireBrace: true});
	if (!reqArgs) {
		ps.pushError(
			"sumstrip requires the answer and the number of squares to fill"
		);
		return false;
	}

	const filledSquaresArg = reqArgs[0];
	let filledSquares = [];
	if (filledSquaresArg) {
		if (filledSquaresArg.text) {
			filledSquares = _.compact(filledSquaresArg.text.split(",")).map(s =>
				parseInt(s)
			);
			filledSquares.forEach(color => {
				if (color > colors.length) {
					ps.pushError(
						`Color ${color} is invalid (there are ${colors.length} total supported colors)`
					);
					return false;
				}
			});
		}
	}

	// Here we generate the child text nodes
	const sections = SumStripsUtil.getContiguousSections(filledSquares);
	// The number of sections is also the number of terms we should have
	const numTerms = (options.terms && options.terms.length) || sections.length;

	const summingTerms = [];
	for (let i = 0; i < numTerms; ++i) {
		let colorName,
			text = "";
		if (i < options.terms.length) {
			colorName = colors[i];
			text = options.terms[i];
		} else {
			colorName = colors[sections[i][0] - 1];
			text = `${sections[i].length - 1}`;
		}
		summingTerms.push({
			type: "text",
			text,
			fill: diagramColors[colorName],
		});
	}
	options.blankTerms.forEach(idx => {
		if (idx < summingTerms.length) {
			summingTerms[idx] = {
				type: "blank",
				blankType: "simple",
				answerSize: 1,
				children: [],
			};
		}
	});

	const allSummingTerms = [];

	// Add + signs to summing terms
	for (let i = 0; i < summingTerms.length - 1; ++i) {
		allSummingTerms.push(summingTerms[i]);
		allSummingTerms.push({
			type: "text",
			text: "+",
			fill: diagramColors.black,
		});
	}

	// And add last and final term
	allSummingTerms.push(_.last(summingTerms));

	const resultTerm = {
		type: "text",
		text: filledSquares.length.toString(),
		fill: diagramColors.black,
	};
	const equalsTerm = {
		type: "text",
		text: "=",
		fill: diagramColors.black,
	};

	const lhs =
		options.equalsPosition === "left" ? [resultTerm] : allSummingTerms;
	const rhs =
		options.equalsPosition === "left" ? allSummingTerms : [resultTerm];
	// Nudge the end of the LHS and the beginning of the RHS to move it away from the equals sign
	if (options.equalsPosition === "left") {
		// Nudge the first term of the RHS to the right away from the equals sign
		_.first(rhs).nudge = 5;
	} else {
		// Nudge the equals sign further away from the terms to the left
		equalsTerm.nudge = 5;
	}

	const allTerms = [...lhs, equalsTerm, ...rhs];

	return [
		{
			type: "sumstrip",
			filledSquares,
			options,
			stripLength: filledSquares.length,
			numTerms,
			lhs,
			rhs,
			equalsTerm,
			children: options.displayEquation ? allTerms : [],
		},
	];
};

// helper function to get correct line width
const getLineWidth = (context, fontSize) => {
	context.save();
	// make line width consistent when font style is different
	// (e.g. 'Courier' in CanvasMath tests)
	context.font = fontSize + "px Roboto, sans-serif";
	const lineWidth = CanvasUtil.getOptimalLineWidth(context);
	context.restore();
	return lineWidth;
};

const sumStripNode = {
	// measure calculates the position for most things, but with
	// a relative position
	draw(rc, x, y, node, measure) {
		const {context} = rc;
		const {options} = node;
		const {
			fontSize,
			spacing,
			maxTermHeight,
			equationMeasure,
			boxes,
			equationStartingXOffset,
			equationStartingYOffset,
			stripStartingXOffset,
			stripStartingYOffset,
			grayBoxTermsIndices,
		} = measure;

		// helper function to draw the boxes for the strip
		const drawStrip = (currX, currY) => {
			const allEdges = [];
			context.save();

			// draw the boxes
			boxes.forEach(box => {
				const {rect, fill, edges} = box;
				allEdges.push(edges);
				context.fillStyle = fill;
				const boxX = rect.x + currX;
				const boxY = rect.y + currY;
				context.fillRect(boxX, boxY, rect.w, rect.h);
			});

			// Draw outlines
			context.strokeStyle = colors.gridLine;
			context.lineWidth = getLineWidth(context, fontSize);
			context.beginPath();
			allEdges.forEach(boxEdges => {
				boxEdges.forEach(edge => {
					context.moveTo(edge[0][0] + currX, edge[0][1] + currY);
					context.lineTo(edge[1][0] + currX, edge[1][1] + currY);
				});
			});
			context.stroke();
			context.restore();
		};

		// helper function to draw equation
		const drawEquation = (currX, currY) => {
			// Get the measurement of the node, then because we can have blanks
			// the space taken up by a text node may actually be more than fontSize
			// so we get the difference and use it to vertically center
			// the text within the largest amount of vertical space the tallest
			// text node used up
			if (options.displayBox) {
				drawDisplayBox(currX, currY);
			}
			context.save();
			node.children.forEach((term, idx) => {
				const ms = equationMeasure.terms[idx];
				const fontSizeDiff = ms.top + ms.bottom - fontSize;
				const height = ms.top + ms.bottom;
				const diff = fontSizeDiff || maxTermHeight - height;
				context.fillStyle = term.fill;
				rc.drawNode(rc, currX + ms.nudge, currY + diff / 2, term, ms);
				currX += ms.width + spacing + ms.nudge;
			});
			context.restore();
		};

		// helper function to draw display box around equation terms
		const drawDisplayBox = (currX, currY) => {
			const termMeasures = _.at(equationMeasure.terms, grayBoxTermsIndices);
			const leftTerm = _.first(termMeasures);
			const rightTerm = _.last(termMeasures);
			let leftSide = leftTerm.offsetX;
			let rightSide = rightTerm.offsetX + rightTerm.width;
			leftSide += currX;
			rightSide += currX;

			const rect = {
				x: leftSide,
				y: currY - fontSize / 2,
				w: rightSide - leftSide,
				h: maxTermHeight,
			};

			context.save();
			context.fillStyle = schemeColors.equationViewBackground;
			// These are basically magic numbers, figured out by tweaking them and seeing what looks "nice"
			// This makes the background larger in area than the equation so there is extra space
			DrawRectUtil.drawRoundedRect(
				context,
				rect.x - fontSize / 4,
				rect.y - fontSize / 6,
				rect.w + fontSize / 2,
				rect.h + fontSize / 3,
				fontSize / 2.5
			);
			context.fill();
			context.restore();
		};

		let currX = x;
		let currY = y;

		// if equation is drawn above strips
		if (options.displayEquation && options.equationAbove) {
			currX = x + equationStartingXOffset;
			currY = y + equationStartingYOffset;
			drawEquation(currX, currY);
			currY += equationMeasure.h;
		}

		// draw strip boxes
		currX = x + stripStartingXOffset;
		currY += stripStartingYOffset;
		drawStrip(currX, currY);

		// if equation is drawn below strips
		if (options.displayEquation && !options.equationAbove) {
			currX = x + equationStartingXOffset;
			currY = y + measure.stripMeasure.h / 2;
			drawEquation(currX, currY);
		}
	},

	measure: function(rc, fontInfo, props, node) {
		const sizeScaleFactor = 1.8;

		const scaledFontInfo = _.cloneDeep(fontInfo);
		scaledFontInfo.size *= sizeScaleFactor;
		let baseSize = scaledFontInfo.size;
		const spacing = baseSize / 5;

		const {options} = node;
		const {context} = rc;
		const lineWidth = getLineWidth(context, fontInfo.size);

		// Get the measure for the strip, which is just the
		// baseSize (width of the boxes) * number of boxes
		const stripMeasure = {
			w: baseSize * node.stripLength,
			h: baseSize,
		};

		// Compute edges for boxes
		// Edges are relative to the top-left corner of the strip
		// We'll save the left, top, and bottom edges for each square
		// then save the right-side edge of the very last square
		// so we don't store edges more than once, as drawing the edge
		// more than once will make that edge appear visually thicker
		let currX = 0;
		const boxes = [];

		for (let i = 0; i < node.stripLength; ++i) {
			const rect = {
				x: currX,
				y: 0,
				w: baseSize,
				h: baseSize,
			};
			const points = [
				[currX, 0],
				[currX + rect.w, 0],
				[currX + rect.w, rect.h],
				[currX, rect.h],
			];
			const edges = [
				[points[0], points[3]], // left edge
				[points[0], points[1]], // top edge
				[points[2], points[3]], // bottom edge
			];

			// for the first box, add extra lines to fill any gap in the left corners
			if (i === 0) {
				edges.push(
					[
						[(-lineWidth * 2) / 3, 0],
						[0, 0], // top left corner
					],
					[
						[(-lineWidth * 2) / 3, baseSize],
						[0, baseSize], // bottom left corner
					]
				);
			}

			const colorValue = node.filledSquares[i];
			const fillColor =
				colorValue === 0
					? diagramColors.white
					: diagramColors[colors[node.filledSquares[i] - 1]];
			boxes.push({
				rect,
				points,
				edges,
				fill: fillColor,
			});
			currX += baseSize;
		}

		// For the very last box, push in the right-hand side edge
		_.last(boxes).edges.push(
			[
				[currX, 0],
				[currX, baseSize],
			],
			// also push in lines to fill in gap in the right corners
			[
				[currX, 0],
				[currX + (lineWidth * 2) / 3, 0], // top right corner
			],
			[
				[currX, baseSize],
				[currX + (lineWidth * 2) / 3, baseSize], // bottom right corner
			]
		);

		// Get the measure for the equation
		const equationMeasure = {
			w: 0,
			h: 0,
			terms: [],
		};

		let maxTermHeight = 0;
		let maxTermTop = 0,
			maxTermBottom = 0;

		// size adjustment for equation (slightly smaller)
		const equationSizeScaleFactor = 1.3;
		scaledFontInfo.size = fontInfo.size * equationSizeScaleFactor;
		baseSize = scaledFontInfo.size;

		if (options.displayEquation) {
			let totalEquationWidth = 0;
			let currXOffset = 0;

			// Extend the cached measurement for the text node with the
			// relative x-offset and the nudge this text node may need
			node.children.forEach((termNode, idx) => {
				const ms = rc.measureNode(rc, scaledFontInfo, props, termNode);
				const {nudge = 0} = termNode;
				equationMeasure.terms.push(
					_.extend({}, ms, {
						offsetX: currXOffset + nudge,
						nudge,
					})
				);

				totalEquationWidth += ms.width + spacing;
				currXOffset += ms.width + spacing + nudge;
				maxTermHeight = Math.max(maxTermHeight, ms.top + ms.bottom);
				maxTermTop = Math.max(maxTermTop, ms.top);
				maxTermBottom = Math.max(maxTermBottom, ms.bottom);
			});
			totalEquationWidth -= spacing;

			equationMeasure.w = totalEquationWidth;
			equationMeasure.h = maxTermHeight;
		}

		// extra padding to ensure the full drawing gets rendered (scales by font size)
		const extraYPadding = fontInfo.size;
		const extraXPadding = lineWidth;
		const spaceBetween = 20 + extraYPadding;

		let totalHeight = stripMeasure.h;
		if (options.displayEquation) {
			totalHeight += spaceBetween + equationMeasure.h;
		}
		const totalWidth =
			Math.max(stripMeasure.w, equationMeasure.w) + extraXPadding;

		const top = totalHeight / 2;
		const bottom = totalHeight / 2;

		let equationStartingYOffset = 0,
			stripStartingYOffset = 0;

		// This is kind of ugly, but what's going on here is that the drawing starts at (0,top)
		// and this needs to be centered in the top, so this is the relative offset to where
		// we need to start rendering the equation/strip for it to be vertically centered
		if (options.equationAbove) {
			equationStartingYOffset =
				0 - top + (top - equationMeasure.h) / 2 + baseSize / 2;
			stripStartingYOffset = (bottom - stripMeasure.h) / 2;
		} else {
			equationStartingYOffset = (bottom - equationMeasure.h) / 2 + baseSize / 2;
			stripStartingYOffset = 0 - top;
			if (options.displayEquation) {
				stripStartingYOffset += (top - stripMeasure.h) / 2;
			}
		}

		const equationStartingXOffset = (totalWidth - equationMeasure.w) / 2;
		const stripStartingXOffset = (totalWidth - stripMeasure.w) / 2;

		// If equalsPosition === "left" gray background is over RHS
		// otherwise it is over the LHS
		// Use length of LHS vs RHS and index into the equationMeasures
		// if gray box is on left, starts at 0 ends at lhs.length - 1
		// if box is on right, starts at lhs.length + 1 and ends with the last one
		let grayBoxTermsIndices = null;
		// The box must be offset to the RHS if on the right side of the equation
		if (options.equalsPosition === "left") {
			grayBoxTermsIndices = _.range(node.lhs.length + 1, node.children.length);
		} else {
			grayBoxTermsIndices = _.range(0, node.lhs.length);
		}

		return {
			top,
			bottom,
			width: totalWidth,
			maxTermHeight,
			stripMeasure,
			equationMeasure,
			totalWidth,
			totalHeight,
			fontSize: baseSize,
			spacing,
			boxes,
			equationStartingXOffset,
			equationStartingYOffset,
			stripStartingXOffset,
			stripStartingYOffset,
			grayBoxTermsIndices,
		};
	},

	readAloud: "",
};

module.exports = {
	nodes: {
		sumstrip: sumStripNode,
	},
	parsers: {
		mathCommands: {
			sumstrip: parser,
		},
	},
};
